import type { ReactNode } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@monksflow/monks-ui';
import { ChevronDownIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

export interface CollapsibleSectionProps {
  title: string;
  /**
   * `sm` steps the heading down a rank, for a surface that already names itself above these
   * sections. Two headings at the same size make the column's own title read as one more section
   * rather than as the thing they are parts of.
   */
  size?: 'md' | 'sm';
  /** One control beside the title, right-aligned. Usually a ghost icon button. */
  action?: ReactNode;
  /** Controlled state. Left out, the section opens and folds on its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  children: ReactNode;
  'data-testid'?: string;
}

/**
 * A named part of a column or a rail, under a heading that folds it away.
 *
 * Each section folds on its own rather than as an accordion: the folders, the tags, the pictures and
 * the typefaces are not alternatives to each other, and putting one away is about seeing more of the
 * next one rather than choosing between them. All of them start open, so nothing is hidden on
 * arrival.
 *
 * The action sits beside the trigger rather than inside it: a button holding a button is invalid,
 * and adding a folder is not the same gesture as folding the list of them. The fixed header height
 * keeps the space under the title identical whether or not a section supplies one.
 *
 * The heading is flush with the surface's own padding while its rows carry theirs, so a row's hover
 * and selected fills extend past the text. That is a property of the fill, not an alignment the
 * title has any reason to copy.
 */
export const CollapsibleSection = ({
  title,
  size = 'md',
  action,
  className,
  children,
  ...rest
}: CollapsibleSectionProps) => (
  <Collapsible defaultOpen className={twMerge('flex flex-col gap-4', className)} {...rest}>
    <div className="flex h-8 shrink-0 items-center justify-between gap-2">
      <CollapsibleTrigger className="group flex min-w-0 items-center gap-2 text-left">
        <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-out group-data-[state=closed]:-rotate-90" />
        <h3
          className={`truncate text-foreground ${size === 'sm' ? 'type-heading-xs' : 'type-heading-sm'}`}
        >
          {title}
        </h3>
      </CollapsibleTrigger>
      {action}
    </div>

    <CollapsibleContent>{children}</CollapsibleContent>
  </Collapsible>
);
